import type { ReactNode, SyntheticEvent } from 'react';
import type { Address } from 'viem';
import { useIsMobile } from '@/modules/use-window-width';
import icon1 from './icons/1.png';
import icon2 from './icons/2.png';
import icon3 from './icons/3.png';
import icon4 from './icons/4.png';
import icon5 from './icons/5.png';
import icon6 from './icons/6.png';
import icon7 from './icons/7.png';
import icon8 from './icons/8.png';
import icon9 from './icons/9.png';
import icon10 from './icons/10.png';
const icons = [
  icon1,
  icon2,
  icon3,
  icon4,
  icon5,
  icon6,
  icon7,
  icon8,
  icon9,
  icon10,
] as const;

// A round user avatar image. `size` is CSS px; a [desktop, mobile] pair
// switches to the mobile value at viewport width ≤767px. A null user keeps
// the box (visibility: hidden), so surrounding layout never shifts while the
// user is loading. A missing avatar and a failed image load both fall back to
// one of ten bundled icons picked deterministically from the address, so a
// user keeps one stable fallback identity across renders and sessions.
export default function Avatar({
  size,
  user,
}: {
  size: [number, number] | number;
  user: null | { address: Address | null; avatar: null | string };
}): ReactNode {
  const [sizeDesktop, sizeMobile] = Array.isArray(size) ? size : [size, size];
  const isMobile = useIsMobile();
  const resolvedSize = isMobile ? sizeMobile : sizeDesktop;
  // Cheap pure computation — same string for the same fields, so key/src
  // stay value-stable without memoization.
  const avatarSrc = user && getAvatarSrc(user);
  return (
    <img
      key={avatarSrc}
      className="rounded-full"
      style={{
        width: `${resolvedSize}px`,
        height: `${resolvedSize}px`,
        visibility: user ? undefined : 'hidden',
      }}
      onError={(e) => user && onImageError(e, user)}
      src={avatarSrc ?? undefined}
    />
  );
}

// null address (walletless SSO account) seeds the shared placeholder icon.
function getRandomAvatar(address: Address | null): string {
  const addressLowerCased = (address ?? '').toLowerCase();
  const idx = Array.from(addressLowerCased).reduce(
    (acc, c) => acc + Number(c.codePointAt(0)),
    0,
  );
  return icons[idx % icons.length] ?? icons[0];
}

function getAvatarSrc({
  avatar,
  address,
}: {
  avatar: string | null;
  address: Address | null;
}): string {
  // Twitter encodes the avatar variant in the filename ("normal" = 48px,
  // "bigger" = 73px); the rewrite requests the higher-resolution one.
  if (avatar?.startsWith('https://pbs.twimg.com') === true) {
    return avatar.replace('normal', 'bigger');
  }
  return absolutizeAvatarUrl(avatar) ?? getRandomAvatar(address);
}

// Backend avatar fields carry either an absolute URL or a backend-relative
// path. null/'' resolves to null — a missing avatar never fabricates a URL;
// absolute http(s) URLs pass through untouched; anything else is prefixed
// with VITE_BACKEND_URL_BASE (environment precondition).
export function absolutizeAvatarUrl(avatar: string | null): string | null {
  if (avatar == null || avatar === '') {
    return null;
  }
  return /^https?:\/\//.test(avatar) ? avatar : (
      `${import.meta.env.VITE_BACKEND_URL_BASE}/${avatar}`
    );
}

function onImageError(
  e: SyntheticEvent<HTMLImageElement>,
  { address }: { address: Address | null },
) {
  e.currentTarget.src = getRandomAvatar(address);
}
